import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  ShoppingCart as ShopIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CreditCard as CreditCardIcon,
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  LocalOffer as OfferIcon,
} from '@mui/icons-material';
import { useLoading } from '../../contexts/LoadingContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useTranslation } from 'react-i18next';

interface CreditPackage {
  id: string;
  name: string;
  rideCount: number;
  originalPrice: number;
  finalPrice: number;
  discountPercentage: number;
  popular: boolean;
}

interface CreditTransaction {
  id: string;
  transactionType: string;
  description?: string;
  createdAt: string;
  amount: number;
  direction: 'credit' | 'debit' | 'neutral';
}

const CreditManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [creditBalance, setCreditBalance] = useState(0);
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [promoTransaction, setPromoTransaction] = useState<CreditTransaction | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadCreditData();
  }, []);

  const loadCreditData = async () => {
    setIsLoading(true);
    try {
      const [balanceRes, packagesRes, transactionsRes] = await Promise.all([
        apiService.get('/credits/balance'),
        apiService.get('/credits/packages'),
        apiService.get('/credits/transactions', { limit: 10 })
      ]);

      if (balanceRes.success && balanceRes.data) {
        setCreditBalance(balanceRes.data.totalCredits || 0);
        const promoTx = (balanceRes.data.recentTransactions || []).find(
          (tx: CreditTransaction) => tx.transactionType === 'grant'
        );
        setPromoTransaction(promoTx || null);
      }

      if (packagesRes.success && packagesRes.data) {
        const apiBaseRate = packagesRes.data.baseRate;
        const normalizedPackages = (packagesRes.data.packages || []).map((pkg: any, index: number) => {
          const originalPrice = pkg.originalPrice ?? (apiBaseRate ? apiBaseRate * pkg.rideCount : pkg.finalPrice);

          // Custom override for discounts as requested: 5%, 7%, 9%
          let discountPercentage = 0;
          if (index === 1) discountPercentage = 5;
          else if (index === 2) discountPercentage = 7;
          else if (index === 3) discountPercentage = 9;

          return {
            id: pkg.id,
            name: pkg.name,
            rideCount: pkg.rideCount,
            originalPrice,
            finalPrice: pkg.finalPrice ?? pkg.price,
            discountPercentage,
            popular: pkg.popular ?? pkg.isPopular
          } as CreditPackage;
        });
        setCreditPackages(normalizedPackages);
      }

      if (transactionsRes.success && transactionsRes.data) {
        const txs: CreditTransaction[] = transactionsRes.data.transactions || [];
        setTransactions(txs);
        const promoTx = txs.find(tx => tx.transactionType === 'grant');
        setPromoTransaction(promoTx || null);
      }
    } catch (error) {
      console.error('Load credit data error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchaseCredits = (creditPackage: CreditPackage) => {
    navigate('/payment', { state: { package: creditPackage } });
  };

  const confirmPurchase = async () => {
    if (!selectedPackage) return;

    setLoading(true, t('creditManagementPage.processing'));
    setPurchaseDialogOpen(false);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update balance
      setCreditBalance(prev => prev + selectedPackage.rideCount);

      // Add transaction
      const newTransaction: CreditTransaction = {
        id: Date.now().toString(),
        transactionType: 'purchase',
        amount: selectedPackage.rideCount,
        description: `${t('creditManagementPage.creditsPurchased')} - ${selectedPackage.name}`,
        createdAt: new Date().toISOString(),
        direction: 'credit'
      };
      setTransactions(prev => [newTransaction, ...prev]);

      // Show success message
      alert(t('creditManagementPage.purchaseSuccess', { count: selectedPackage.rideCount, price: selectedPackage.finalPrice.toFixed(2) }));
    } catch (error) {
      console.error('Purchase error:', error);
      alert(t('creditManagementPage.purchaseFailed'));
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const getCreditPackageIcon = (rideCount: number) => {
    if (rideCount <= 10) return '📦';
    if (rideCount <= 25) return '📦📦';
    return '📦📦📦';
  };

  const getTransactionIcon = (type: string) => {
    switch ((type || '').toLowerCase()) {
      case 'purchase':
        return '💳';
      case 'ride_post':
        return '🚗';
      case 'refund':
        return '💰';
      case 'grant':
        return '🎁';
      default:
        return '📝';
    }
  };

  const getTransactionLabel = (transaction: CreditTransaction) => {
    if (transaction.description) {
      return transaction.description;
    }

    switch ((transaction.transactionType || '').toLowerCase()) {
      case 'grant':
        return t('creditManagementPage.promoAdded');
      case 'ride_post':
        return t('creditManagementPage.ridePostDeduction');
      case 'refund':
        return t('creditManagementPage.creditsRefunded');
      case 'purchase':
        return t('creditManagementPage.creditsPurchased');
      default:
        return t('creditManagementPage.creditActivity');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsedDate);
  };

  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 } }}>
      <Container maxWidth="lg">
        {/* Header with vibrant aesthetic */}
        <Box sx={{
          mb: 6,
          p: 4,
          borderRadius: '32px',
          background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
          color: 'white',
          boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)',
          textAlign: 'center'
        }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: '900',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.02em',
              textShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          >
            💳 {t('creditManagementPage.title')}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', mt: 1, opacity: 0.9, fontWeight: 500 }}>
            {t('creditManagementPage.subtitle')} ⚡
          </Typography>
        </Box>

        {promoTransaction && (
          <Alert
            severity="success"
            sx={{
              mb: 4,
              borderRadius: '24px',
              fontSize: '1.4rem',
              fontWeight: 'bold',
              p: 2,
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            }}
          >
            🎁 {t('creditManagementPage.promo', { amount: Math.abs(promoTransaction.amount || 0), date: formatDate(promoTransaction.createdAt) })}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Current Balance */}
          <Box sx={{ flex: { xs: '1', md: '0 0 33.333%' } }}>
            <Card
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                borderRadius: '32px',
                boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.4)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <CardContent>
                <BalanceIcon sx={{ fontSize: '3.5rem', mb: 2 }} />
                <Typography sx={{ mb: 1, fontSize: '1.5rem', fontWeight: '800', opacity: 0.9 }}>
                  {t('creditManagementPage.balance')}
                </Typography>
                <Typography sx={{ fontWeight: '900', mb: 1, fontSize: '4.5rem', lineHeight: 1 }}>
                  {creditBalance}
                </Typography>
                <Typography sx={{ mb: 2, fontSize: '1.8rem', fontWeight: '700' }}>
                  {creditBalance !== 1 ? t('creditManagementPage.credits') : t('creditManagementPage.credit')}
                </Typography>

                {creditBalance === 0 && (
                  <Alert
                    severity="warning"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: '16px',
                      fontSize: '1.2rem',
                      '& .MuiAlert-icon': { color: 'white', fontSize: '1.8rem' }
                    }}
                  >
                    {t('creditManagementPage.needsCredits')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Credit Packages */}
          <Box sx={{ flex: { xs: '1', md: '0 0 66.666%' } }}>
            <Paper elevation={0} sx={{
              p: { xs: 3, md: 5 },
              borderRadius: '32px',
              border: '1px solid rgba(0,0,0,0.05)',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              height: '100%'
            }}>
              <Typography sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, fontSize: '2rem', fontWeight: '900' }}>
                <ShopIcon sx={{ fontSize: '2.5rem', color: '#6366f1' }} />
                {t('creditManagementPage.purchase')}
              </Typography>
              <Typography sx={{ mb: 4, fontSize: '1.4rem', color: 'text.secondary', fontWeight: '500' }}>
                {t('creditManagementPage.choosePackage')}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 3 }}>
                {creditPackages.map((pkg) => (
                  <Box key={pkg.id}>
                    <Card
                      elevation={0}
                      sx={{
                        position: 'relative',
                        height: '100%',
                        borderRadius: '24px',
                        border: pkg.popular ? '3px solid #6366f1' : '1px solid rgba(0,0,0,0.1)',
                        background: pkg.popular ? 'rgba(99, 102, 241, 0.03)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          borderColor: '#6366f1',
                          background: 'rgba(99, 102, 241, 0.05)'
                        }
                      }}
                    >
                      {pkg.popular && (
                        <Chip
                          label={t('creditManagementPage.popular')}
                          sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            fontWeight: '900',
                            fontSize: '1rem',
                            bgcolor: '#6366f1',
                            color: 'white',
                            p: 2,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            zIndex: 2
                          }}
                        />
                      )}

                      {pkg.discountPercentage > 0 && (
                        <Chip
                          label={`${pkg.discountPercentage}${t('creditManagementPage.off')}`}
                          sx={{
                            position: 'absolute',
                            top: 20,
                            left: 15,
                            fontWeight: '900',
                            fontSize: '1rem',
                            bgcolor: '#ef4444',
                            color: 'white',
                            zIndex: 2
                          }}
                        />
                      )}

                      <CardContent sx={{ textAlign: 'center', pt: 8, pb: 3 }}>

                        <Typography sx={{ fontWeight: '900', mb: 1, fontSize: '1.8rem' }}>
                          {pkg.rideCount} {t('creditManagementPage.credits')}
                        </Typography>

                        <Typography sx={{ fontWeight: '900', color: '#6366f1', mb: 1, fontSize: '2.5rem' }}>
                          {formatCurrency(pkg.finalPrice)}
                        </Typography>

                        {pkg.originalPrice !== pkg.finalPrice && (
                          <Typography
                            sx={{
                              textDecoration: 'line-through',
                              color: 'text.secondary',
                              mb: 1,
                              fontSize: '1.2rem',
                              fontWeight: '600'
                            }}
                          >
                            {formatCurrency(pkg.originalPrice)}
                          </Typography>
                        )}

                        <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary', fontWeight: '700' }}>
                          {formatCurrency(pkg.finalPrice / pkg.rideCount)} {t('creditManagementPage.perCredit')}
                        </Typography>
                      </CardContent>

                      <CardActions sx={{ justifyContent: 'center', pb: 4, px: 3 }}>
                        <Button
                          variant="contained"
                          onClick={() => handlePurchaseCredits(pkg)}
                          fullWidth
                          sx={{
                            borderRadius: '16px',
                            py: 1.5,
                            fontSize: '1.3rem',
                            fontWeight: '900',
                            textTransform: 'none',
                            background: pkg.popular ? 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' : 'rgba(0,0,0,0.05)',
                            color: pkg.popular ? 'white' : 'text.primary',
                            boxShadow: pkg.popular ? '0 10px 15px -3px rgba(99, 102, 241, 0.3)' : 'none',
                            '&:hover': {
                              background: pkg.popular ? 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)' : 'rgba(0,0,0,0.1)',
                            }
                          }}
                        >
                          {t('creditManagementPage.purchaseButton')} →
                        </Button>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 3 }}>
          {/* How Credits Work */}
          <Box sx={{ flex: 1 }}>
            {/* How Credits Work */}
            <Box sx={{ flex: 1 }}>
              <Paper elevation={0} sx={{
                p: 4,
                borderRadius: '32px',
                border: '1px solid rgba(0,0,0,0.05)',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                height: '100%'
              }}>
                <Typography sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2, fontSize: '2rem', fontWeight: '900' }}>
                  <InfoIcon sx={{ fontSize: '2.5rem', color: '#3b82f6' }} />
                  {t('creditManagementPage.howCreditsWork')} 🚀
                </Typography>

                <List sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
                  {[
                    { icon: <CarIcon sx={{ fontSize: '2.5rem', color: '#6366f1' }} />, title: t('creditManagementPage.postingRides'), desc: t('creditManagementPage.postingRidesDesc') },
                    { icon: <MoneyIcon sx={{ fontSize: '2.5rem', color: '#10b981' }} />, title: t('creditManagementPage.refunds'), desc: t('creditManagementPage.refundsDesc') },
                    { icon: <CheckIcon sx={{ fontSize: '2.5rem', color: '#3b82f6' }} />, title: t('creditManagementPage.noExpiration'), desc: t('creditManagementPage.noExpirationDesc') }
                  ].map((item, idx) => (
                    <ListItem key={idx} sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '20px', p: 2 }}>
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '1.5rem', fontWeight: '900' }}>{item.title}</Typography>}
                        secondary={<Typography sx={{ fontSize: '1.1rem', color: 'text.secondary', mt: 0.5 }}>{item.desc}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          </Box>

          {/* Transaction History */}
          <Box sx={{ flex: 1 }}>
            {/* Transaction History */}
            <Box sx={{ flex: 1 }}>
              <Paper elevation={0} sx={{
                p: 4,
                borderRadius: '32px',
                border: '1px solid rgba(0,0,0,0.05)',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                height: '100%'
              }}>
                <Typography sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2, fontSize: '2rem', fontWeight: '900' }}>
                  <HistoryIcon sx={{ fontSize: '2.5rem', color: '#a855f7' }} />
                  {t('creditManagementPage.history')} 📜
                </Typography>

                {transactions.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', py: 6, fontSize: '1.5rem', color: 'text.secondary', fontWeight: '600' }}>
                    {t('creditManagementPage.noTransactions')}
                  </Typography>
                ) : (
                  <List sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
                    {transactions.slice(0, 10).map((transaction, index) => {
                      const amountValue = transaction.amount ?? 0;
                      const amountColor = transaction.direction === 'credit'
                        ? '#10b981'
                        : transaction.direction === 'debit'
                          ? '#ef4444'
                          : 'text.primary';
                      return (
                        <React.Fragment key={transaction.id}>
                          <ListItem sx={{ px: 2, py: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '20px' }}>
                            <ListItemIcon>
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: '16px',
                                  backgroundColor: 'white',
                                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '2rem'
                                }}
                              >
                                {getTransactionIcon(transaction.transactionType)}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: '900' }}>{getTransactionLabel(transaction)}</Typography>}
                              secondary={<Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>{formatDate(transaction.createdAt)}</Typography>}
                              sx={{ ml: 2 }}
                            />
                            <Typography
                              sx={{
                                fontWeight: '900',
                                fontSize: '1.8rem',
                                color: amountColor
                              }}
                            >
                              {amountValue > 0 ? '+' : ''}{amountValue}
                            </Typography>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Purchase Confirmation Dialog */}
        <Dialog
          open={purchaseDialogOpen}
          onClose={() => setPurchaseDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '32px', p: 2 } }}
        >
          <DialogTitle sx={{ fontSize: '2rem', fontWeight: '900', textAlign: 'center' }}>
            {t('creditManagementPage.confirmPurchase')} 💳
          </DialogTitle>
          <DialogContent>
            {selectedPackage && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ mb: 1, fontSize: '3.5rem' }}>
                  {getCreditPackageIcon(selectedPackage.rideCount)}
                </Typography>
                <Typography sx={{ mb: 3, fontSize: '2.5rem', fontWeight: '900', color: '#6366f1' }}>
                  {selectedPackage.name}
                </Typography>

                <Box sx={{ bgcolor: 'rgba(0,0,0,0.03)', p: 3, borderRadius: '24px', mb: 3 }}>
                  <Typography sx={{ mb: 1.5, fontSize: '1.6rem', fontWeight: '700' }}>
                    {t('creditManagementPage.creditsLabel')} <span style={{ color: '#10b981', fontWeight: '900' }}>{selectedPackage.rideCount}</span>
                  </Typography>
                  <Typography sx={{ mb: 0, fontSize: '1.6rem', fontWeight: '700' }}>
                    {t('creditManagementPage.priceLabel')} <span style={{ color: '#f59e0b', fontWeight: '900' }}>{formatCurrency(selectedPackage.finalPrice)}</span>
                  </Typography>
                </Box>

                {selectedPackage.discountPercentage > 0 && (
                  <Alert severity="success" sx={{ mb: 3, borderRadius: '16px', fontSize: '1.3rem', fontWeight: '700' }}>
                    {t('creditManagementPage.saving', { discount: selectedPackage.discountPercentage })} 🔥
                  </Alert>
                )}

                <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary', fontWeight: '600' }}>
                  {t('creditManagementPage.redirect')}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 0, justifyContent: 'center', gap: 2 }}>
            <Button onClick={() => setPurchaseDialogOpen(false)} sx={{ fontSize: '1.4rem', px: 4, py: 1.5, borderRadius: '20px', fontWeight: '800' }}>
              {t('creditManagementPage.cancel')}
            </Button>
            <Button
              onClick={confirmPurchase}
              variant="contained"
              sx={{
                fontSize: '1.6rem',
                px: 6,
                py: 1.5,
                borderRadius: '20px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                boxShadow: '0 10px 15px rgba(99, 102, 241, 0.3)'
              }}
            >
              {t('creditManagementPage.proceed')} 🚀
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CreditManagementPage;